import * as React from "react";
import { useState } from "react";
import { URLS, useAccountSettings } from "@/utils";
import { Category } from "@/services/bedrock";
import { Button, Card, Combobox, Skeleton } from "@/components/ui";
import { WebComponents } from "@/interfaces";
import { SuperPageCard } from ".";
import { useHistory } from "react-router-dom";
import { useSuperPageSearch } from "@/utils/useSuperPageSearch";
import { useNewCategories } from "@/utils/useNewCategories";

const SuperPagesListPreview: React.FC = () => {
  const history = useHistory();
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.VIDEO_PAGES);
  const { searchSuperPages } = useSuperPageSearch();
  const { categories } = useNewCategories("SUPER_PAGE");

  const { superPages, isFetching } = searchSuperPages({
    categoryId: selectedCategories.length > 0 ? selectedCategories.map(c => c.id) : undefined,
    extraFields: ["totalViews", "totalClicks", "assignmentsOverview", "categories"],
    page: 1,
    perPage: 7,
    sort: "updated_date",
    order: "desc",
  });

  const content = superPages?.content ?? [];
  const totalSuperPages = superPages?.pagination?.totalCount ?? 0;

  const goToSuperPageCreateForm = () => {
    history.push(URLS.coach.newSuperPage);
  };

  const goToSuperPagesList = () => {
    history.push(URLS.coach.superPages);
  };

  if (content.length === 0 && selectedCategories.length === 0 && withWritePermission) {
    return (
      <div className="relative">
        <div
          className={`flex flex-col justify-center items-center h-58 gap-4 bg-gray-100 rounded-lg px-8 py-10 ${
            isFetching ? "opacity-0" : "opacity-100"
          }`}
        >
          <div className="text-center text-2xl font-bold">Scale your video strategy</div>
          <div className="text-center text-base max-w-2xl mx-auto">
            You haven't launched any Super Pages yet. Create your first campaign to assign a Video Page template,
            script, CTA to your players, making it effortless to engage their pipeline.
          </div>
          <Button children="Create a Super Page" onClick={goToSuperPageCreateForm} />
        </div>
        {isFetching && (
          <div className="flex flex-col gap-4 absolute top-0 left-0 w-full h-full">
            <div className="flex items-center flex-wrap justify-between gap-4">
              <div className="flex gap-4 items-center flex-wrap flex-auto">
                <Skeleton className="h-9 w-20" />
                <Skeleton className="h-9 w-20" />
              </div>
              <div className="flex gap-4 items-center flex-wrap">
                <Skeleton className="h-9 w-40" />
              </div>
            </div>
            <Skeleton className="flex-auto" />
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center flex-wrap justify-between gap-4">
          <div className="text-xl font-semibold text-foreground">Super Pages</div>
          {withWritePermission && <Button children="New Super Page" onClick={goToSuperPageCreateForm} />}
        </div>
        {categories.length > 0 && (
          <div className="flex flex-row gap-2 overflow-x-auto no-scrollbar">
            <Combobox
              className="min-w-50 max-w-80"
              searchable
              placeholder="Filter by Category"
              items={categories}
              value={selectedCategories}
              itemToStringLabel={item => item.name}
              onValueChange={value => setSelectedCategories(value)}
              multiple
              maxChips="auto"
            />
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {content.map(page => (
            <SuperPageCard key={`super-page-${page.id}`} superPage={page} disabled={isFetching} />
          ))}

          <Card
            disabled={isFetching}
            className="w-full h-full cursor-pointer ring-0 hover:shadow-md bg-gray-50 border-border border-dashed border-2 flex flex-col items-center justify-center p-4"
            onClick={goToSuperPagesList}
          >
            <Card.Content className="flex flex-col gap-2 items-center justify-center text-center">
              <div className="text-2xl font-bold text-primary">{totalSuperPages}</div>
              <div className="text-lg text-foreground">Total Super Pages</div>
              <div className="text-sm text-primary underline">View all</div>
            </Card.Content>
          </Card>
        </div>
      </div>
    );
  }
};

export { SuperPagesListPreview };
